// @flow
import React, {useState, useEffect, useCallback} from 'react';
import SortableList, {SortableItem, SortableKnob} from 'react-easy-sort';

import {makeStyles, IconButton, Button} from '@material-ui/core';
import {arrayMove} from '../../../../utils/array';

import type {Subcategory, SubcategoryItem} from '../../../../entities';
import {colors} from '../../../../assets/styles/colors';
import {useToggle} from '../../../../utils/react';
import {useSubCategoriesItem} from '../../../sub-categories/utils';
import {DragIndicator, DeleteOutline, Edit, Add} from '@material-ui/icons';
import TiendadaTooltip from '../../../../elements/tiendada-tooltip';
import SubcategoryItemModal from './SubcategoryItemModal';
import {SUBCATEGORY_ITEM_ENABLED} from './SubcategoryList';
import ConfirmModal from '../../../../elements/Modals/ConfirmModal';

const useStyles = makeStyles(theme => ({
  subCategoriesContainer: {
    marginLeft: '2rem',
    marginTop: '.5rem',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '.75rem',
    overflow: 'hidden',
    marginBottom: '1rem',
    gap: '.5rem',
  },
  addSubcategoryButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: colors.grey200,
    padding: '.5rem',
    borderRadius: '.75rem',
    gap: '1rem',
    textTransform: 'none',
    textAlign: 'center',
    color: colors.grey800,
  },
  subcategoryItem: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    background: colors.grey200,
    padding: '.25rem',
    paddingLeft: '0',
    borderRadius: '.5rem',
    position: 'relative',
  },
  '@global': {
    '.dragged': {
      transform: 'rotate(8deg)',
      boxShadow: `0px 8px 24px rgba(66, 64, 64, 0.12), 0 0 0 1px ${colors.primary}`,
      backgroundColor: colors.lightPurple,
      zIndex: 1000,
    },
  },
  itemContent: {
    display: 'flex',
    alignItems: 'center',

    justifyContent: 'space-between',
    flexGrow: 1,
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
  },
  horizontalDivider: {
    width: 2,
    height: '35px',
    backgroundColor: '#D4D4DD',
    margin: 'auto 12px',
  },
  itemName: {
    color: colors.grey800,
    fontSize: '.75rem',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flexGrow: 1,
  },
  trashIcon: {
    color: '#E54D7B',
    float: 'right',
    cursor: 'pointer',
  },
  editIcon: {
    color: '#717190',
    float: 'right',
    cursor: 'pointer',
  },
  iconButton: {
    padding: '.25rem',
  },
  dragIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.grey600,
    alignSelf: 'normal',

    '&:hover': {
      cursor: 'grab',
      color: colors.primary,
    },
    '&:active': {
      cursor: 'grabbing',
    },
    [theme.breakpoints.up('sm')]: {
      padding: '.25rem',
    },

    [theme.breakpoints.up('md')]: {
      padding: '.5rem',
    },
  },
  containerDraggable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.25rem',
  },
}));

type Props = {
  subcategory: Subcategory,
  subcategoriesItem: SubcategoryItem[],
};

function SubcategoryItemListComponent(props: Props) {
  const {subcategory} = props;
  const classes = useStyles();

  const [subcategoriesItem, setSubcategoriesItem] = useState(
    props.subcategoriesItem
  );

  const {
    createSubCategoryItem,
    deleteSubCategoryItem,
    editSubCategoryItem,
    sortSubCategoryItem,
  } = useSubCategoriesItem();

  const [subcategorySelected, setSubcategorySelected] = useState(undefined);
  const [modalOpen, toggleModalOpen] = useToggle(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<?string>(null);

  useEffect(() => {
    setSubcategoriesItem(props.subcategoriesItem);
  }, [props.subcategoriesItem]);

  const handleSortEnd = useCallback(
    (oldIndex: number, newIndex: number) => {
      const oldId = subcategoriesItem[oldIndex].id;
      const newId = subcategoriesItem[newIndex].id;

      const sortedSections = arrayMove(subcategoriesItem, oldIndex, newIndex);

      sortSubCategoryItem(oldId, newId, subcategory.id);

      setSubcategoriesItem(sortedSections);
    },
    [subcategoriesItem, sortSubCategoryItem, subcategory.id]
  );

  const handleDeleteClick = useCallback((itemId: string) => {
    setItemToDelete(itemId);
    setConfirmDeleteOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (itemToDelete) {
      deleteSubCategoryItem(itemToDelete);
      setItemToDelete(null);
    }
    setConfirmDeleteOpen(false);
  }, [itemToDelete, deleteSubCategoryItem]);

  const handleEditItem = useCallback((item: SubcategoryItem) => {
    setSubcategorySelected(item);
  }, []);

  const handleAddItem = useCallback(() => {
    setSubcategorySelected(undefined);
    toggleModalOpen();
  }, [toggleModalOpen]);

  return (
    <>
      <div className={classes.subCategoriesContainer}>
        <SortableList
          className={classes.containerDraggable}
          draggedItemClassName="dragged"
          onSortEnd={handleSortEnd}
        >
          {subcategoriesItem?.map(item => (
            <SortableItem key={item.id}>
              <div className={classes.subcategoryItem}>
                <div className={classes.itemContent}>
                  <SortableKnob>
                    <span className={classes.dragIndicator}>
                      <DragIndicator fontSize="small" />
                    </span>
                  </SortableKnob>
                  <span className={classes.itemName}>{item.name}</span>
                  <div className={classes.rightSection}>
                    <TiendadaTooltip title="Editar item" placement="top">
                      <IconButton
                        onClick={() => handleEditItem(item)}
                        className={classes.iconButton}
                        size="small"
                      >
                        <Edit fontSize="small" className={classes.editIcon} />
                      </IconButton>
                    </TiendadaTooltip>
                    <TiendadaTooltip title="Eliminar item" placement="top">
                      <IconButton
                        onClick={() => handleDeleteClick(item.id)}
                        className={classes.iconButton}
                        size="small"
                      >
                        <DeleteOutline
                          fontSize="small"
                          className={classes.trashIcon}
                        />
                      </IconButton>
                    </TiendadaTooltip>
                  </div>
                </div>
              </div>
            </SortableItem>
          ))}
        </SortableList>

        {SUBCATEGORY_ITEM_ENABLED && (
          <Button
            className={classes.addSubcategoryButton}
            startIcon={<Add />}
            onClick={handleAddItem}
          >
            Agregar item
          </Button>
        )}
      </div>

      {modalOpen && (
        <SubcategoryItemModal
          onClose={toggleModalOpen}
          open={modalOpen}
          onSubmit={name => {
            createSubCategoryItem({
              subCategoryId: subcategory.id,
              name,
            }).then(() => {
              toggleModalOpen();
            });
          }}
          subcategory={subcategory.name}
        />
      )}

      {subcategorySelected && (
        <SubcategoryItemModal
          onClose={() => {
            setSubcategorySelected(undefined);
          }}
          open={!!subcategorySelected}
          defaultName={subcategorySelected.name}
          onSubmit={name => {
            editSubCategoryItem(subcategorySelected.id, name).then(() => {
              setSubcategorySelected(undefined);
            });
          }}
          subcategory={subcategory.name}
        />
      )}

      <ConfirmModal
        open={confirmDeleteOpen}
        onClose={() => {
          setConfirmDeleteOpen(false);
          setItemToDelete(null);
        }}
        onCancel={() => {
          setConfirmDeleteOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="¿Eliminar item?"
        message="¿Estás seguro de que deseas eliminar este item? Esta acción no se puede deshacer."
        confirmButtonLabel="Eliminar"
        cancelButtonLabel="Cancelar"
      />
    </>
  );
}

const SubcategoryItemList = React.memo(SubcategoryItemListComponent);

export default SubcategoryItemList;
